import { Link, useNavigate, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { api } from "../api/client";
import type { FieldSurvey } from "../api/types";
import { DataTable } from "../components/DataTable";
import { imageUrl } from "../components/EvidenceViewer";
import { Icon } from "../components/Icon";
import { PageHeader, Panel } from "../components/Layout";
import { MapPanel } from "../components/MapPanel";
import { KpiTile } from "../components/Charts";
import { EmptyState, QueryState, SkeletonBlock } from "../components/States";
import { ago, chainage, dateTime, num } from "../lib/format";
import { COLORS } from "../styles/themeColors";

interface SurveysResp { active: FieldSurvey[]; recent: FieldSurvey[]; phone_url: string }

function Processing({ s }: { s: FieldSurvey }) {
  if (!s.privacy_pending && !s.ai_pending) return <span className="chip verified">Processed</span>;
  return (
    <span className="muted" style={{ fontSize: 13 }}>
      {[s.privacy_pending ? `${s.privacy_pending} awaiting blur` : "", s.ai_pending ? `${s.ai_pending} awaiting AI` : ""].filter(Boolean).join(" · ")}
    </span>
  );
}

export function SurveysPage() {
  const nav = useNavigate();
  const q = useQuery({ queryKey: ["surveys"], queryFn: () => api<SurveysResp>("/api/surveys"), refetchInterval: 30_000 });
  const ready = useQuery({ queryKey: ["surveys", "readiness"], queryFn: () => api<{ checks: { name: string; ok: boolean; detail: string }[] }>("/api/surveys/readiness") });

  return (
    <>
      <PageHeader title="Field surveys" subtitle="Surveys being captured now, and the latest to sync." />
      <div className="split">
        <Panel title="Active surveys" id="sv-active" flush>
          <QueryState query={q} skeleton={<div className="panel-body"><SkeletonBlock rows={3} /></div>}>
            {(d) => (
              <DataTable<FieldSurvey>
                rows={d.active}
                rowKey={(r) => r.survey_uuid}
                onRowClick={(r) => nav(`/surveys/${r.survey_uuid}/live`)}
                caption="Active surveys"
                empty={<EmptyState title="No surveys are being captured">Start one from a phone with the link on the right.</EmptyState>}
                columns={[
                  { key: "road", label: "Road", render: (r) => <Link className="row-link" to={`/surveys/${r.survey_uuid}/live`} onClick={(e) => e.stopPropagation()}>{r.road_code} — {r.road_name}</Link> },
                  { key: "who", label: "Surveyor", render: (r) => r.surveyor || "—" },
                  { key: "start", label: "Started", render: (r) => dateTime(r.started_at) },
                  { key: "km", label: "So far", align: "right", render: (r) => `${r.km.toFixed(2)} km` },
                  { key: "frames", label: "Images", align: "right", render: (r) => num(r.frames) },
                  { key: "fix", label: "Last GPS fix", render: (r) => <span className={r.last_fix_age_s !== null && r.last_fix_age_s < 300 ? "" : "muted"}>{ago(r.last_fix_age_s)}</span> },
                ]}
              />
            )}
          </QueryState>
        </Panel>
        <Panel title="Start a survey" id="sv-start">
          <div className="stack">
            <p>On the survey phone, open the field app, sign in, and tap <b>Start a new road survey</b>.</p>
            <div className="row" style={{ flexWrap: "nowrap" }}>
              <input className="input mono" readOnly value={`${location.origin}/field`} aria-label="Field app link" onFocus={(e) => e.target.select()} />
              <button className="btn" onClick={() => navigator.clipboard?.writeText(`${location.origin}/field`).catch(() => undefined)}>Copy</button>
            </div>
            <Link className="btn btn-primary" to="/field/start">Start a survey on this device</Link>
            <p className="muted" style={{ fontSize: 13 }}>Recording keeps working with no signal; GPS and images wait on the phone and sync when it's back online. For a pilot drive, tick the pilot option and pick a 5–10 km target.</p>
            <h3 style={{ fontSize: 14, marginTop: 8 }}>Readiness</h3>
            <QueryState query={ready} skeleton={<SkeletonBlock rows={3} />}>
              {(r) => (
                <ul className="check-list" style={{ listStyle: "none", padding: 0, margin: 0 }}>
                  {r.checks.map((c) => (
                    <li key={c.name} className="check-item">
                      <span className={c.ok ? "ok" : "no"} aria-hidden="true">{c.ok ? "✓" : "✕"}</span>
                      <span><b>{c.name}</b>{c.ok ? "" : " — missing"}<small className="muted" style={{ display: "block" }}>{c.detail}</small></span>
                    </li>
                  ))}
                </ul>
              )}
            </QueryState>
          </div>
        </Panel>
      </div>
      <Panel title="Recent surveys" id="sv-recent" flush>
        <QueryState query={q} skeleton={<div className="panel-body"><SkeletonBlock rows={6} /></div>}>
          {(d) => (
            <DataTable<FieldSurvey>
              rows={d.recent}
              rowKey={(r) => r.survey_uuid}
              onRowClick={(r) => r.status === "COMPLETED" && nav(`/roads/${r.survey_uuid}`)}
              caption="Recent surveys"
              empty={<EmptyState title="No surveys yet" />}
              columns={[
                { key: "road", label: "Road", render: (r) => <>{r.road_code} — {r.road_name}</> },
                { key: "st", label: "Status", render: (r) => <span className={`chip ${r.status === "COMPLETED" ? "verified" : "neutral"}`}>{r.status.toLowerCase()}</span> },
                { key: "who", label: "Surveyor", render: (r) => r.surveyor || "—" },
                { key: "end", label: "Ended", render: (r) => dateTime(r.ended_at ?? r.started_at) },
                { key: "km", label: "Length", align: "right", render: (r) => `${r.km.toFixed(2)} km` },
                { key: "img", label: "Images", align: "right", render: (r) => num(r.frames) },
                { key: "proc", label: "Processing", render: (r) => <Processing s={r} /> },
              ]}
            />
          )}
        </QueryState>
      </Panel>
    </>
  );
}

interface Live {
  survey: FieldSurvey;
  track: [number, number][];
  latest: { speed_kph: number; accuracy_m: number | null; krb_road: string; rics_uid: string | null; chainage_m: number | null; at: string } | null;
  frames: { frame_uuid: string; chainage_m: number | null; captured_at_device: string; privacy_status: string; rdd_count: number | null; asset_count: number | null }[];
  detections: { detection_uuid: string; code: string; class_name: string; confidence: number; chainage_m: number | null; detected_at: string }[];
}

export function LiveSurveyPage() {
  const { surveyId = "" } = useParams();
  const q = useQuery({ queryKey: ["surveys", surveyId, "live"], queryFn: () => api<Live>(`/api/surveys/${surveyId}/live`), refetchInterval: 10_000 });
  return (
    <QueryState query={q} skeleton={<SkeletonBlock rows={10} />}>
      {(d) => {
        const s = d.survey;
        const last = d.track[d.track.length - 1];
        return (
          <div className="hud">
            <PageHeader
              crumbs={[{ label: "Field surveys", to: "/surveys" }, { label: `${s.road_code} ${s.road_name}` }]}
              title={<>{s.road_code} — {s.road_name}</>}
              subtitle={<><span className={`hud-live${s.status === "ACTIVE" && (s.last_fix_age_s ?? 1e9) < 300 ? " on" : ""}`}>{s.status === "ACTIVE" ? ((s.last_fix_age_s ?? 1e9) < 300 ? "Live" : "Waiting for GPS") : s.status.toLowerCase()}</span>{` · started ${dateTime(s.started_at)}${s.surveyor ? ` by ${s.surveyor}` : ""} · updates every 10 seconds`}</>}
              actions={s.status === "COMPLETED" ? <Link className="btn" to={`/roads/${s.survey_uuid}`}><Icon name="roads" />Open road</Link> : null}
            />
            <div className="kpis">
              <KpiTile label="Distance" value={`${s.km.toFixed(2)} km`} note={d.latest ? chainage(d.latest.chainage_m) : undefined} />
              <KpiTile label="Speed" value={d.latest ? `${d.latest.speed_kph} km/h` : "—"} />
              <KpiTile label="GPS accuracy" value={d.latest?.accuracy_m ? `±${Math.round(d.latest.accuracy_m)} m` : "—"} note={`Last fix ${ago(s.last_fix_age_s)}`} />
              <KpiTile label="Images" value={num(s.frames)} note={s.privacy_pending ? `${s.privacy_pending} awaiting blur` : undefined} />
              <KpiTile label="Matched road" value={d.latest?.krb_road || "—"} note={d.latest?.rics_uid ? `RICS ${d.latest.rics_uid}` : "No RICS match"} />
            </div>
            <div className="split">
              <Panel title="Track" id="lv-map">
                <MapPanel label="Survey track" height={420} tiles="dark" lines={d.track.length ? [{ id: "t", points: d.track, color: COLORS.accent }] : []}
                          pins={last ? [{ id: "now", lat: last[0], lon: last[1], color: COLORS.good, label: "Latest position" }] : []} />
              </Panel>
              <div className="stack" style={{ gap: 16 }}>
                <Panel title="Latest images" id="lv-frames">
                  {d.frames.length ? (
                    <div className="image-grid" style={{ gridTemplateColumns: "repeat(auto-fill, minmax(140px, 1fr))" }}>
                      {d.frames.map((f) => (
                        <div key={f.frame_uuid} className="image-card" style={{ cursor: "default" }}>
                          <img src={imageUrl(f.frame_uuid, true)} alt={`Image at ${chainage(f.chainage_m)}`} loading="lazy" />
                          <span className="meta"><span>{chainage(f.chainage_m)}</span></span>
                        </div>
                      ))}
                    </div>
                  ) : <EmptyState title="No images yet" />}
                </Panel>
                <Panel title="AI detections" id="lv-det">
                  {d.detections.length ? (
                    <ul style={{ margin: 0, paddingLeft: 18 }}>
                      {d.detections.map((x) => <li key={x.detection_uuid}><b>{x.code}</b> {x.class_name.replace(/_/g, " ")} · <span className="mono">{chainage(x.chainage_m)}</span> · {Math.round(x.confidence * 100)}%</li>)}
                    </ul>
                  ) : (s.status === "ACTIVE" ? <EmptyState title="No detections yet">AI runs as images arrive.</EmptyState> : <EmptyState title="No AI detections on this survey" />)}
                </Panel>
              </div>
            </div>
          </div>
        );
      }}
    </QueryState>
  );
}
